// pfs-empty-state.ts — generischer Leerzustand (PFS-589, Epic PFS-591).
//
// Loest das ueber alle Cut-Apps duplizierte .empty-Muster ab (mednet-identity
// .empty 3x + SVG-Literale, practice/scheduling analog): zentrierte Card mit
// Icon + fetter Titel-Zeile + optionaler Sekundaer-Message + optionaler Aktion.
//
// Brandbar/dark via --pfs-color-*-Tokens (vererben durch die Shadow-Grenze).
//
// Attribute (alle data-*):
//   data-title     Fette Titel-Zeile (reaktiv), z. B. "Keine Benutzer vorhanden"
//   data-message   Optionale Sekundaer-Zeile (leiser/kleiner). Leer → versteckt.
//   data-variant   card (Default, Surface+Border) | plain (nur zentrierter Inhalt)
//
// Slots:
//   icon    eigenes <svg slot="icon"> (sonst Default-Inbox-Icon)
//   action  z. B. <button slot="action"> (leer → versteckt)
//
// Beispiel:
//   <pfs-empty-state data-title="Keine Benutzer" data-message='Fuer „abc" nichts gefunden.'>
//     <button slot="action" class="btn primary">Anlegen</button>
//   </pfs-empty-state>
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const DEFAULT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z"/>
</svg>`;

const STYLES = `
:host { display: block; }
/* Das hidden-Attribut respektieren: :host{display:block} ueberschreibt sonst
   die UA-Regel [hidden]{display:none} (hoehere Spezifitaet) → der Leerzustand
   bliebe trotz hidden sichtbar (PFS-693-Fund: doppeltes Empty unter der Liste). */
:host([hidden]) { display: none; }
.es { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; text-align: center; padding: 3rem 1rem; color: var(--pfs-color-on-surface); box-sizing: border-box; }
.es.card { background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); border-radius: var(--pfs-radius-md, 0.5rem); }
.es-icon { display: flex; justify-content: center; }
.es-icon ::slotted(svg), .es-icon svg { width: 2.5rem; height: 2.5rem; color: var(--pfs-color-on-surface-subtle); }
.es-title { font-weight: 600; margin: 0; color: var(--pfs-color-on-surface-muted); }
.es-title:empty { display: none; }
.es-msg { font-size: 0.875rem; color: var(--pfs-color-on-surface-subtle); }
.es-msg:empty { display: none; }
.es-action { display: flex; justify-content: center; }
`;

defineComponent({
  tagName: "pfs-empty-state",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const wrap = document.createElement("div");
    // Struktur einmal bauen (Slots stabil); paint() aktualisiert nur Klasse + Text.
    wrap.innerHTML = `
      <div class="es-icon"><slot name="icon">${DEFAULT_ICON}</slot></div>
      <p class="es-title"></p>
      <p class="es-msg"></p>
      <div class="es-action"><slot name="action"></slot></div>`;
    root.appendChild(wrap);

    const titleEl = wrap.querySelector(".es-title") as HTMLElement;
    const msgEl = wrap.querySelector(".es-msg") as HTMLElement;
    const paint = () => {
      wrap.className = host.dataset.variant === "plain" ? "es" : "es card";
      titleEl.textContent = host.dataset.title ?? "";
      msgEl.textContent = host.dataset.message ?? "";
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-title", "data-message", "data-variant"],
    });
  },
});
